import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper, TableContainer, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { COLORS_ADMIN_PAGE, COLORS_CONFIG_PAGE } from '../../conf/constants';
import { MunicipalityStats } from '../types';

interface ReportTableProps {
  municipalities: MunicipalityStats[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
}

const ReportTable: React.FC<ReportTableProps> = ({ municipalities, searchTerm, onSearchChange }) => {
  // Filter municipalities based on search term
  const filteredMunicipalities = municipalities.filter((municipality) =>
    Object.values(municipality).some((value) =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <Box sx={{ 
      flex: 1, 
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Search Section */}
      <Box sx={{ 
        p: 3, 
        borderBottom: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
        display: 'flex',
        gap: 2,
        alignItems: 'center'
      }}>
        <TextField
          size="small"
          placeholder="Search Report..."
          variant="outlined"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 300,
            bgcolor: COLORS_CONFIG_PAGE.background,
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' },
            },
            borderRadius: 3,
          }}
        />
      </Box>

      {/* Table Section */}
      <TableContainer component={Paper} sx={{ flex: 1, boxShadow: 'none' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell><strong>Municipality</strong></TableCell>
              <TableCell><strong>Total Users</strong></TableCell>
              <TableCell><strong>New Users This Month</strong></TableCell>
              <TableCell><strong>Verified Contractors</strong></TableCell>
              <TableCell><strong>Total Orders</strong></TableCell>
              <TableCell><strong>New Orders This Month</strong></TableCell>
              <TableCell><strong>Profit</strong></TableCell>
              <TableCell><strong>Loss</strong></TableCell>
              <TableCell><strong>Revenue</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMunicipalities.length > 0 ? (
              filteredMunicipalities.map((municipality, index) => (
                <TableRow key={index}>
                  <TableCell>{municipality.municipality || '-'}</TableCell>
                  <TableCell>{municipality.totalUsers ?? '-'}</TableCell>
                  <TableCell>{municipality.newUsersThisMonth ?? '-'}</TableCell>
                  <TableCell>{municipality.verifiedContractorsRatio ? `${municipality.verifiedContractorsRatio}%` : '-'}</TableCell>
                  <TableCell>{municipality.totalOrders ?? '-'}</TableCell>
                  <TableCell>{municipality.newOrdersThisMonth ?? '-'}</TableCell>
                  <TableCell>{municipality.profits ?? '-'}</TableCell>
                  <TableCell>{municipality.loss ?? '-'}</TableCell>
                  <TableCell>{municipality.revenue ?? '-'}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography>No data available</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ReportTable;